import React, { useEffect, useState } from "react";
import { Button, Input, Table, notification } from "antd";
import debounce from "lodash.debounce";
import AuthService from "../../../base/services/authentication.service";
import { httpService } from "../../../base/services/httpService.service";
import { UserRolesEnum, translateRoleName } from "../../auth/userRoles.enum";
import { translateGenderName } from "../../../base/utils/genderLabels";
import { formatDateString, convertTZ } from "../../../base/utils/timezoneConverter";
import { AddNewMemberTeam, AddMemeberSuccess, AddMemberError } from "../../../assets/icons";
import SortArrows from "../../../components/SortArrows";
import AddMemberModal from "./components/AddMemberModal";
import DeleteMemberModal from "./components/DeleteMemberModal";
import { useTranslation } from "../../../i18n/LanguageContext";
import "./components/teamMembersList.scss";

// Same header treatment as the Modules/Experts/Organizations/Approvers
// tables (see src/app/pages/admin/survey/SurveysList.js): #DDE9FF bg,
// #325899 text.
const headerStyle = {
   backgroundColor: "#DDE9FF",
   color: "#325899",
   fontFamily: "Poppins",
   fontSize: "12px",
   fontWeight: 500,
   lineHeight: "16px"
};

const components = {
   header: {
      cell: (props) => <th {...props} style={headerStyle} />
   }
};

const getInitial = (fullName) => (fullName ? fullName.trim().charAt(0).toUpperCase() : "U");

// Same search-icon treatment as OrganizationListFilters.js's search input.
const SearchIcon = () => (
   <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="6" cy="6" r="5" stroke="#979797" strokeWidth="1" />
      <path d="M9.5 9.5L13 13" stroke="#979797" strokeWidth="1" strokeLinecap="round" />
   </svg>
);

// Same path as the shared DeleteRowTable asset, but grey instead of that
// asset's baked-in blue (#4158CF) - kept local so the shared icon used
// elsewhere isn't recolored.
const DeleteIcon = () => (
   <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
      <path
         d="M1.42859 5.00153H18.5714M6.42859 5.00153V4.28725C6.42859 3.34005 6.80486 2.43163 7.47463 1.76186C8.1444 1.09209 9.05282 0.71582 10 0.71582C10.9472 0.71582 11.8556 1.09209 12.5254 1.76186C13.1952 2.43163 13.5714 3.34005 13.5714 4.28725V5.00153M7.85716 9.28934V15.0058M12.1429 9.28934V15.0058M3.57145 5.00153H16.4286V17.8587C16.4286 18.2375 16.278 18.601 16.0102 18.8688C15.7423 19.1367 15.3789 19.2872 15 19.2872H5.00002C4.62113 19.2872 4.25777 19.1367 3.98986 18.8688C3.72196 18.601 3.57145 18.2375 3.57145 17.8587V5.00153Z"
         stroke="#979797"
         strokeWidth="1.25"
         strokeLinecap="round"
         strokeLinejoin="round"
      />
   </svg>
);

// Org-only "Team member list" page (Organization Management > Team). Kept
// entirely separate from src/app/pages/workspace/teams/TeamList.js, which is
// a different feature ("Experts", under the Assessments module) that also
// happens to be reachable by International/National Org roles - the two
// used to collide on the same /teams/members route.
function TeamMembersList() {
   const { t } = useTranslation();
   const isSuperAdmin = AuthService.isAuthorized([UserRolesEnum.SuperAdmin]);

   const [memberList, setMemberList] = useState([]);
   const [pagination, setPagination] = useState({ pageSize: 10, pageNumber: 1, total: 0 });
   const [searchQuery, setSearchQuery] = useState("");
   const [selectedRowKeys, setSelectedRowKeys] = useState([]);
   const [showAddMemberModal, setShowAddMemberModal] = useState(false);
   const [showDeleteModal, setShowDeleteModal] = useState(false);
   const [selectedID, setSelectedID] = useState("-1");
   const [deleteMemberLoading, setDeleteMemberLoading] = useState(false);

   const fetchMemberList = (name = "") => {
      if (isSuperAdmin) return;
      httpService.get(
         `/StaffManagement?pageSize=${pagination.pageSize}&pageNumber=${pagination.pageNumber}&name=${name}`,
         (res) => {
            setMemberList(res.data);
            setPagination((prev) => ({ ...prev, total: res.metaData.totalItemCount }));
         }
      );
   };

   const debouncedFetchMemberList = debounce((name) => fetchMemberList(name), 300);

   useEffect(() => {
      debouncedFetchMemberList(searchQuery);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [searchQuery, pagination.pageSize, pagination.pageNumber]);

   const onPageChange = (pageNumber, pageSize) => {
      setPagination((prev) => ({ ...prev, pageNumber, pageSize }));
   };

   const handleShowDelete = (id) => {
      setSelectedID(id);
      setShowDeleteModal(true);
   };

   const notifySuccess = (title, description) => {
      notification.open({
         description: (
            <div className="custom-notification-container">
               <div className="custom-notification-image-container">
                  <img src={AddMemeberSuccess} alt="AddMemeberSuccess" />
               </div>
               <div className="custom-notification-text-container">
                  <h3>{title}</h3>
                  <p>{description}</p>
               </div>
            </div>
         ),
         placement: "top"
      });
   };

   const notifyError = (title, description) => {
      notification.open({
         description: (
            <div className="custom-notification-container-error">
               <div className="custom-notification-image-container">
                  <img src={AddMemberError} alt="AddMemberError" />
               </div>
               <div className="custom-notification-text-container-error">
                  <h3>{title}</h3>
                  <p>{description}</p>
               </div>
            </div>
         ),
         placement: "top"
      });
   };

   const handleDelete = async () => {
      setDeleteMemberLoading(true);
      try {
         await httpService.delete(`/StaffManagement/${selectedID}`, { id: selectedID });
         fetchMemberList(searchQuery);
         notifySuccess(
            t("team-member-removed-success-title"),
            t("team-member-removed-success-description")
         );
      } catch (error) {
         notifyError(t("toast-oops-title"), t("team-member-remove-error-description"));
      } finally {
         setDeleteMemberLoading(false);
         setShowDeleteModal(false);
      }
   };

   const columns = [
      {
         title: <div className="header-table-text">{t("fullname-column")}</div>,
         dataIndex: "fullName",
         key: "fullName",
         sorter: (a, b) => (a.fullName || "").localeCompare(b.fullName || ""),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (fullName, record) => (
            <div className="team-member-name-cell">
               <div className="team-member-avatar">{getInitial(fullName)}</div>
               <div>
                  <div className="team-member-name">{fullName}</div>
                  <div className="team-member-email">{record.email}</div>
               </div>
            </div>
         )
      },
      {
         title: <div className="header-table-text">{t("birthdate-label")}</div>,
         dataIndex: "birthDate",
         key: "birthDate",
         sorter: (a, b) => new Date(a.birthDate || 0) - new Date(b.birthDate || 0),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (birthDate) => (
            <div className="team-member-cell-text">
               {birthDate ? formatDateString(convertTZ(birthDate)) : "-"}
            </div>
         )
      },
      {
         title: <div className="header-table-text">{t("gender-label")}</div>,
         dataIndex: "gender",
         key: "gender",
         sorter: (a, b) => (a.gender?.name || "").localeCompare(b.gender?.name || ""),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (gender) => (
            <div className="team-member-cell-text">
               {(gender && gender.name && translateGenderName(gender.name, t)) || "-"}
            </div>
         )
      },
      {
         title: <div className="header-table-text">{t("role-label")}</div>,
         dataIndex: "role",
         key: "role",
         sorter: (a, b) => (a.role || "").localeCompare(b.role || ""),
         sortIcon: (sortOrder) => <SortArrows sortOrder={sortOrder} />,
         render: (role) => <div className="team-member-cell-text">{translateRoleName(role, t)}</div>
      },
      {
         title: "",
         key: "action",
         fixed: "right",
         width: 20,
         render: (text, record) => (
            <span className="team-member-delete-icon" onClick={() => handleShowDelete(record.id)}>
               <DeleteIcon />
            </span>
         ),
         className: "costum-action-column"
      }
   ];

   return (
      <div className="team-members-page">
         <div className="team-members-card">
            <div className="team-members-top">
               <div className="team-members-header">
                  <h1>{t("team-member-list-title")}</h1>
                  <p>{t("team-member-list-description")}</p>
               </div>
               {!isSuperAdmin && (
                  <Button
                     className="team-members-add-button"
                     type="primary"
                     onClick={() => setShowAddMemberModal(true)}
                  >
                     <img src={AddNewMemberTeam} alt="AddNewMemberTeam" />
                     <div>{t("new-member-button")}</div>
                  </Button>
               )}
            </div>
            <Input
               className="team-members-search"
               autoComplete="off"
               placeholder={t("search-member-by-name-placeholder")}
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
               suffix={<SearchIcon />}
            />
            <div className="team-members-table">
               <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={memberList}
                  rowSelection={{
                     selectedRowKeys,
                     onChange: setSelectedRowKeys
                  }}
                  pagination={{
                     current: pagination.pageNumber,
                     pageSize: pagination.pageSize,
                     total: pagination.total,
                     onChange: onPageChange,
                     showSizeChanger: false
                  }}
                  components={components}
               />
            </div>
         </div>
         <AddMemberModal
            visible={showAddMemberModal}
            onCancel={() => setShowAddMemberModal(false)}
            onOk={() => {
               setShowAddMemberModal(false);
               fetchMemberList(searchQuery);
            }}
         />
         <DeleteMemberModal
            visible={showDeleteModal}
            onCancel={() => setShowDeleteModal(false)}
            onOk={handleDelete}
            deleteMemberLoading={deleteMemberLoading}
         />
      </div>
   );
}

export default TeamMembersList;
